import { ModalV2 } from '@dapp/multi-lib/ui/V2Modal';
import { Body2, Body3, Title2, Title4 } from 'components/Typography';
import { useTranslation } from 'react-i18next';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { formatUnits, parseUnits } from 'utils/units';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { createDepositValidation } from 'structure/pv2/vault/utils/createValidationSchema';
import { Button } from '@dapp/multi-lib/sdaoui/Button';
import { Icon } from '@dapp/multi-lib/sdaoui/Icon';
import { HorizontalLine } from '@dapp/multi-lib/sdaoui/HorizontalLine';
import { ModalDetailsRow } from 'structure/pv2/vault/components/ModalDetailsRow';
import { ButtonIcon } from '@dapp/multi-lib/sdaoui/ButtonIcon';
import { InputTokenAmount } from 'components/form/InputTokenAmount';
import { Collapse } from '@dapp/multi-lib/sdaoui/Collapse';
import { SlippageInput } from 'structure/pv2/vault/components/SlippageInput';
import {
  usePreviewSwap,
  usePreviewNativeSwap,
  useSpenderAllowance,
  useSwap,
  useNativeSwap,
  useIsTokenAllowedForUser,
  usePermittedVault,
} from 'structure/pv2/vault/hooks/useDynaRouterContract';
import {
  calcMinAmountOut,
  getDynaRouterAddress,
  getRiskLevelColorClass,
  getRiskLevelLabel,
  getVaultAPY,
  getVaultAPYPrefix,
  VaultIdentity,
} from 'structure/pv2/vault/utils/vault';
import { useVault } from 'structure/pv2/vault/hooks/useVault';
import { useConnection } from 'wagmi';
import { useToast } from '@dapp/multi-lib/ui/toast';
import { waitForTransaction } from 'utils/waitForTransaction';
import { ethers } from 'ethers';
import {
  formatPercent,
  formatValueToSixDecimals,
} from '@dapp/multi-lib/lib/formatters';
import { DynaVault, Token } from '../../../../api/DynaVaultsAPI';
import { useApprove } from 'utils/hooks/useERC20Contract';
import { getTokenIconUrl } from 'utils/url';
import { SelectToken } from 'structure/pv2/vault/SelectToken';
import { InputBalanceLabel } from 'structure/pv2/vault/containers/InputBalanceLabel';
import { useDebounce } from 'use-debounce';
import { useSwitchNetwork } from 'utils/hooks/useSwitchNetwork';
import { ETH_ZERO_ADDRESS } from 'utils/constants/ethConst';
import { SpendLimitInput } from '../components/SpendLimitInput';
import { getTxErrorMessage } from 'utils/getTxErrorMessage';
import { VaultType } from '../configs/vaultTypes';
import { TokenImage } from 'components/TokenImage';
import { useVaultsAddresses } from 'structure/pv2/vault/hooks/useDynavaultManager';
import { useQueryClient } from '@tanstack/react-query';
import { queryKeys } from 'structure/pv2/vault/configs/queryKeys';
import { useTokenBalance } from 'utils/hooks/useTokenBalance';
import { useTokenPrice } from 'structure/pv2/vault/hooks/useTokenPrice';
import { cx } from 'class-variance-authority';
import { Loader } from '@dapp/multi-lib/ui/loader';
import { twMerge } from 'tailwind-merge';
import { refreshAfterTransaction } from 'structure/pv2/vault/utils/refreshAfterTransaction';
import { canDepositIntoVault } from 'structure/pv2/vault/utils/canDepositIntoVault';
import { requiresAllowanceReset } from 'utils/erc20Approval';
import {
  isNativeBnbDeposit,
  NATIVE_BNB_TOKEN,
} from 'structure/pv2/vault/utils/nativeDeposit';

interface FormData {
  tokenAmount?: string;
  slippage?: number;
  spendingLimit?: string;
}

interface ModalVaultDepositProps {
  open: boolean;
  vaultIdentity: VaultIdentity;
  depositTokenAddress: `0x${string}`;
  depositTokenAmount?: string;
  depositTokenList: Token[];
  expandDetails?: boolean;
  readOnlyInput?: boolean;
  onDepositSuccess?: () => void | Promise<void>;
  onOpenChange: (open: boolean) => void;
}

export const ModalVaultDeposit = (props: ModalVaultDepositProps) => {
  const { t } = useTranslation('vault');
  const {
    vaultIdentity,
    open,
    depositTokenAmount = '0',
    depositTokenAddress,
    depositTokenList,
    onOpenChange,
    onDepositSuccess,
    expandDetails,
    readOnlyInput,
  } = props;
  const vaultQuery = useVault(vaultIdentity);
  const account = useConnection();

  const vaultsAddressesQuery = useVaultsAddresses({
    from: account.address,
    vaultAddress: vaultQuery.data?.address || ETH_ZERO_ADDRESS,
    chainId: vaultIdentity.chainId,
  });

  const whiteListedAddressQuery = useIsTokenAllowedForUser({
    account: (account.address ?? ETH_ZERO_ADDRESS) as `0x${string}`,
    chainId: vaultIdentity.chainId,
    tokenAddress: (vaultQuery.data?.address ??
      ETH_ZERO_ADDRESS) as `0x${string}`,
    userAddress: (account.address ?? ETH_ZERO_ADDRESS) as `0x${string}`,
    contractAddress: (vaultsAddressesQuery.data?.vaultZapper ??
      ETH_ZERO_ADDRESS) as `0x${string}`,
    query: {
      enabled:
        account.address &&
        !!vaultQuery.data &&
        !vaultQuery.data.isClosed &&
        vaultQuery.data?.type === VaultType.INDEX &&
        !!vaultsAddressesQuery.data,
    },
  });

  const whiteListedVaultQuery = usePermittedVault({
    account: (account.address ?? ETH_ZERO_ADDRESS) as `0x${string}`,
    chainId: vaultIdentity.chainId,
    tokenAddress: (vaultQuery.data?.address ??
      ETH_ZERO_ADDRESS) as `0x${string}`,
    userAddress: (account.address ?? ETH_ZERO_ADDRESS) as `0x${string}`,
    contractAddress: (vaultsAddressesQuery.data?.vaultZapper ??
      ETH_ZERO_ADDRESS) as `0x${string}`,
    query: {
      enabled:
        account.address &&
        !!vaultQuery.data &&
        !vaultQuery.data.isClosed &&
        vaultQuery.data?.type === VaultType.YIELD &&
        !!vaultsAddressesQuery.data,
    },
  });

  const isWhiteListed = whiteListedAddressQuery.data;
  const isWhiteListedVault = whiteListedVaultQuery.data;
  const isLoading =
    vaultQuery.isLoading ||
    (!vaultQuery.data?.isClosed &&
      (whiteListedAddressQuery.isLoading ||
        whiteListedVaultQuery.isLoading ||
        vaultsAddressesQuery.isLoading));
  const vaultType = vaultQuery.data?.metadata?.contracts
    ?.type as keyof typeof VaultType;

  return (
    <ModalV2.Root modal={true} open={open} onOpenChange={onOpenChange}>
      <ModalV2.Content
        variant='dark'
        className='min-w-[350px]'
        fullscreen={false}
        onInteractOutside={(event) => {
          event.preventDefault();
        }}
      >
        <ModalV2.BodyTitle
          variant='dark'
          endEnhancer={<ModalV2.Close variant='dark' />}
        >
          <Title2>{t('MODAL_DEPOSIT.TITLE')}</Title2>
        </ModalV2.BodyTitle>
        <HorizontalLine className='my-4 h-[0.2px] border-white-300' />

        {isLoading && (
          <ModalV2.Body variant='dark'>
            <Loader color='white' />
          </ModalV2.Body>
        )}

        {!isLoading && vaultQuery.data?.isClosed && (
          <ModalV2.Body variant='dark'>
            <Body2 className='text-center'>
              {t('MODAL_DEPOSIT.CLOSED_MESSAGE')}
            </Body2>
          </ModalV2.Body>
        )}

        {!isLoading &&
          canDepositIntoVault(vaultQuery.data) &&
          (vaultType === VaultType.INDEX
            ? !isWhiteListed
            : !isWhiteListedVault) && (
            <ModalV2.Body variant='dark'>
              <Body2 className='text-center'>
                {t('MODAL_WITHDRAW.UNAUTHORIZED_MESSAGE')}
              </Body2>
            </ModalV2.Body>
          )}

        {!isLoading &&
          canDepositIntoVault(vaultQuery.data) &&
          (vaultType === VaultType.INDEX
            ? isWhiteListed
            : isWhiteListedVault) &&
          vaultQuery.data && (
            <ModalContent
              expandDetails={expandDetails}
              readOnlyInput={readOnlyInput}
              vault={vaultQuery.data}
              depositTokenAmount={depositTokenAmount}
              depositTokenAddress={depositTokenAddress}
              depositTokenList={depositTokenList}
              onDepositSuccess={onDepositSuccess}
              onOpenChange={onOpenChange}
            />
          )}
      </ModalV2.Content>
    </ModalV2.Root>
  );
};

interface ModalContentProps {
  onOpenChange: (open: boolean) => void;
  onDepositSuccess?: () => void | Promise<void>;
  depositTokenAddress: `0x${string}`;
  depositTokenList: Token[];
  depositTokenAmount?: string;
  vault: DynaVault;
  expandDetails?: boolean;
  readOnlyInput?: boolean;
}

const ModalContent = (props: ModalContentProps) => {
  const {
    vault,
    depositTokenAmount,
    depositTokenAddress,
    depositTokenList,
    expandDetails = true,
    readOnlyInput = false,
    onDepositSuccess,
    onOpenChange,
  } = props;
  const { t } = useTranslation('vault');
  const toast = useToast();
  const queryClient = useQueryClient();

  const account = useConnection();
  const vaultChainId = vault.chainId;
  const tokenOut = vault.address as `0x${string}`;
  const availableDepositTokenList = useMemo(
    () =>
      depositTokenList.some((token) =>
        isNativeBnbDeposit(vaultChainId, token.address)
      ) || !isNativeBnbDeposit(vaultChainId, NATIVE_BNB_TOKEN.address)
        ? depositTokenList
        : [NATIVE_BNB_TOKEN, ...depositTokenList],
    [depositTokenList, vaultChainId]
  );

  const [selectTokenOpen, setSelectTokenOpen] = useState(false);

  const _depositToken = availableDepositTokenList.find(
    (token) => token.address.toLowerCase() === depositTokenAddress.toLowerCase()
  );

  const [depositToken, setDepositToken] = useState<Token>(
    _depositToken as Token
  );
  const isNativeDeposit = isNativeBnbDeposit(
    vaultChainId,
    depositToken.address
  );

  const dynaRouterAddress = getDynaRouterAddress(vault);

  const sharesBalanceQuery = useTokenBalance({
    address: account.address,
    token: tokenOut,
    chainId: vaultChainId,
    query: {
      enabled: true,
    },
  });

  const balanceQuery = useTokenBalance({
    address: account.address,
    token: depositToken.address,
    chainId: vaultChainId,
    query: {
      enabled: true,
    },
  });

  const userBalance = balanceQuery.data?.value || BigInt(0);

  const [slippageOpen, setSlippageOpen] = useState(false);
  const [spendLimitOpen, setSpendLimitOpen] = useState(false);

  const handleOpenChange = (isOpen: boolean) => {
    onOpenChange(isOpen);
  };

  const toggleSpendLimit = () => setSpendLimitOpen((open) => !open);

  const schema = useMemo(() => {
    return createDepositValidation({
      tokenAmount: {
        label: t('FORM.TOKEN_AMOUNT'),
        max: formatUnits(userBalance, depositToken.decimals),
        required: true,
        decimals: depositToken.decimals,
      },
    });
  }, [userBalance, depositToken, t]);

  const form = useForm<FormData>({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: yupResolver(schema) as any,
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      tokenAmount: depositTokenAmount || '0',
      slippage: 0.1,
      spendingLimit: '0',
    },
  });

  const handleChangeSlippage = (value: number) => {
    form.setValue('slippage', value);
    form.trigger('slippage');
  };

  const handleClickMax = () => {
    form.setValue(
      'tokenAmount',
      formatUnits(userBalance, depositToken.decimals)
    );
    form.trigger('tokenAmount');
  };

  const handleTokenChange = (token: Token) => {
    setDepositToken(token);
    form.setValue('tokenAmount', '0');
    setSelectTokenOpen(false);
  };

  const tokenAmount = form.watch('tokenAmount') || '0';
  const slippage = form.watch('slippage');
  const customSpendingLimit = form.watch('spendingLimit');

  const [bnTokenAmount, { isPending: isTokenAmountPending }] = useDebounce(
    parseUnits(tokenAmount, depositToken.decimals),
    400,
    { equalityFn: (prev, next) => prev.eq(next) }
  );

  const [spendingLimit, { isPending: isSpendingLimitPending }] = useDebounce(
    parseUnits(
      Number(customSpendingLimit) > 0
        ? customSpendingLimit ?? '0'
        : tokenAmount,
      depositToken.decimals
    ),
    400,
    { equalityFn: (prev, next) => prev.eq(next) }
  );

  const isDebouncedValid = useMemo(() => {
    return schema.isValidSync({
      tokenAmount: formatUnits(bnTokenAmount, depositToken.decimals),
      slippage,
    });
  }, [bnTokenAmount, depositToken.decimals, schema, slippage]);

  const { chain } = useConnection();
  const isCorrectChain = chain?.id === vaultChainId;

  const tokenPreviewSwap = usePreviewSwap({
    from: (account.address ?? ETH_ZERO_ADDRESS) as `0x${string}`,
    contractAddress: dynaRouterAddress as `0x${string}`,
    chainId: vaultChainId,
    tokenIn: depositToken.address,
    amountIn: bnTokenAmount.toString(),
    tokenOut: tokenOut,
    query: {
      enabled: !isNativeDeposit && isDebouncedValid && isCorrectChain,
    },
  });

  const nativePreviewSwap = usePreviewNativeSwap({
    from: (account.address ?? ETH_ZERO_ADDRESS) as `0x${string}`,
    chainId: vaultChainId,
    tokenIn: depositToken.address,
    amountIn: bnTokenAmount.toString(),
    tokenOut,
    query: {
      enabled: isNativeDeposit && isDebouncedValid && isCorrectChain,
    },
  });

  const previewSwap = isNativeDeposit ? nativePreviewSwap : tokenPreviewSwap;

  const spenderAllowance = useSpenderAllowance({
    contractAddress: dynaRouterAddress as `0x${string}`,
    account: (account.address ?? ETH_ZERO_ADDRESS) as `0x${string}`,
    chainId: vaultChainId,
    tokenAddress: depositToken.address,
    query: {
      enabled: !isNativeDeposit,
    },
  });

  const bnAllowanceAmount = ethers.BigNumber.from(spenderAllowance.data || 0);
  const requiredAllowance =
    !isNativeDeposit && bnTokenAmount.gt(bnAllowanceAmount);
  const allowanceMustBeReset =
    requiredAllowance &&
    requiresAllowanceReset({
      chainId: vaultChainId,
      tokenAddress: depositToken.address,
      currentAllowance: bnAllowanceAmount.toString(),
      nextAllowance: spendingLimit.toString(),
    });

  const approve = useApprove({
    chainId: vaultChainId,
    tokenAmount: allowanceMustBeReset ? '0' : spendingLimit.toString(),
    tokenAddress: depositToken.address,
    spenderAddress: dynaRouterAddress as `0x${string}`,
    query: {
      enabled: isDebouncedValid && requiredAllowance,
    },
  });

  const minAmountOut = calcMinAmountOut(
    previewSwap.data?.amountOut || 0,
    slippage ?? 0
  );
  const isTokenAmountSynced = parseUnits(tokenAmount, depositToken.decimals).eq(
    bnTokenAmount
  );

  const currentDepositTokenRef = useRef(depositToken.address);
  currentDepositTokenRef.current = depositToken.address;

  const tokenSwap = useSwap({
    contractAddress: dynaRouterAddress as `0x${string}`,
    chainId: vaultChainId,
    tokenIn: depositToken.address,
    amountIn: bnTokenAmount.toString(),
    tokenOut: tokenOut,
    minAmountOut: minAmountOut.toString(),
    routerAddress: (previewSwap.data?.selectedRouter ??
      ETH_ZERO_ADDRESS) as `0x${string}`,
    from: (account.address ?? ETH_ZERO_ADDRESS) as `0x${string}`,
    to: (account.address ?? ETH_ZERO_ADDRESS) as `0x${string}`,
    swapData: previewSwap.data?.swapData ?? [],
    query: {
      enabled:
        !isNativeDeposit &&
        isDebouncedValid &&
        !!previewSwap.data?.swapData &&
        !!previewSwap.data?.selectedRouter &&
        !requiredAllowance &&
        isCorrectChain,
    },
  });

  const nativeSwap = useNativeSwap({
    chainId: vaultChainId,
    tokenIn: depositToken.address,
    amountIn: bnTokenAmount.toString(),
    tokenOut,
    minAmountOut: minAmountOut.toString(),
    routerAddress: (previewSwap.data?.selectedRouter ??
      ETH_ZERO_ADDRESS) as `0x${string}`,
    from: (account.address ?? ETH_ZERO_ADDRESS) as `0x${string}`,
    to: (account.address ?? ETH_ZERO_ADDRESS) as `0x${string}`,
    swapData: previewSwap.data?.swapData ?? [],
    query: {
      enabled:
        isNativeDeposit &&
        isDebouncedValid &&
        !!previewSwap.data?.swapData &&
        !!previewSwap.data?.selectedRouter &&
        isCorrectChain,
    },
  });

  const activeSwap = isNativeDeposit ? nativeSwap : tokenSwap;

  const approveRequest = approve.prepare.data?.request;
  const depositRequest = activeSwap.prepare.data?.request;
  const depositPreparationError =
    activeSwap.gasQuery.error ?? activeSwap.prepare.error;

  const handleDeposit = async () => {
    try {
      const submittedAmount = parseUnits(
        form.getValues('tokenAmount') || '0',
        depositToken.decimals
      );
      const submittedTokenAddress = depositToken.address;
      const submittedTokenDecimals = depositToken.decimals;
      const submittedSlippage = form.getValues('slippage') ?? 0;

      if (isTokenAmountPending() || !submittedAmount.eq(bnTokenAmount)) {
        throw new Error('Deposit amount is still updating. Please try again.');
      }

      const freshPreview = await previewSwap.fetchFresh();
      if (freshPreview.amountOut <= 0) {
        throw new Error('Unable to fetch a fresh deposit quote.');
      }

      const latestAmount = parseUnits(
        form.getValues('tokenAmount') || '0',
        submittedTokenDecimals
      );
      const inputsChanged =
        !latestAmount.eq(submittedAmount) ||
        currentDepositTokenRef.current !== submittedTokenAddress ||
        (form.getValues('slippage') ?? 0) !== submittedSlippage;

      if (inputsChanged) {
        throw new Error(
          'Deposit inputs changed while preparing the transaction. Please try again.'
        );
      }

      const freshMinAmountOut = calcMinAmountOut(
        freshPreview.amountOut,
        submittedSlippage
      );
      const result = isNativeDeposit
        ? await nativeSwap.contract.writeContractAsync(
            (
              await nativeSwap.prepareFresh({
                amountIn: submittedAmount.toString(),
                tokenOut,
                minAmountOut: freshMinAmountOut.toString(),
                routerAddress: freshPreview.selectedRouter,
                swapData: freshPreview.swapData,
              })
            ).request
          )
        : await tokenSwap.contract.writeContractAsync(
            (
              await tokenSwap.prepareFresh({
                amountIn: submittedAmount.toString(),
                tokenOut,
                minAmountOut: freshMinAmountOut.toString(),
                swapData: freshPreview.swapData,
              })
            ).request
          );

      await waitForTransaction({
        chainId: vaultChainId,
        hash: result,
        connectorId: account.connector?.id,
      });

      toast({
        title: t('TOAST.DEPOSIT_SUCCESS_TITLE'),
        description: `Code: ${result}`,
        type: 'success',
      });

      void queryClient.invalidateQueries({
        queryKey: queryKeys.vaultScope,
      });

      void refreshAfterTransaction([
        () => sharesBalanceQuery.refetch(),
        () => onDepositSuccess?.(),
      ]);

      handleOpenChange(false);
    } catch (error) {
      console.log('### error', error);

      const errorMessage = getTxErrorMessage(
        error as Error,
        t('TOAST.FAILED_TO_REQUEST_DEPOSIT_DESC')
      );

      toast({
        title: t('TOAST.FAILED_TO_REQUEST_DEPOSIT_TITLE'),
        description: errorMessage,
        type: 'error',
      });
    }
  };

  const handleApprove = async () => {
    if (approve.prepare.error || !approveRequest) {
      const errorMessage = approve.prepare.error
        ? getTxErrorMessage(
            approve.prepare.error,
            t('TOAST.FAILED_TO_REQUEST_APPROVE_DESC')
          )
        : t('TOAST.FAILED_TO_REQUEST_APPROVE_DESC');

      toast({
        title: t('TOAST.FAILED_TO_REQUEST_APPROVE_TITLE'),
        description: errorMessage,
        type: 'error',
      });
      return;
    }

    try {
      let result = await approve.contract.writeContractAsync(approveRequest);

      await waitForTransaction({
        chainId: vaultChainId,
        hash: result,
        connectorId: account.connector?.id,
      });

      if (allowanceMustBeReset) {
        const finalApproval = await approve.prepareFresh(
          spendingLimit.toString()
        );
        result = await approve.contract.writeContractAsync(
          finalApproval.request
        );

        await waitForTransaction({
          chainId: vaultChainId,
          hash: result,
          connectorId: account.connector?.id,
        });
      }

      toast({
        title: t('TOAST.APPROVE_SUCCESS_TITLE'),
        description: `Code: ${result}`,
        type: 'success',
      });

      await spenderAllowance.refetch();
    } catch (error) {
      console.log('### approve error', error);
      void spenderAllowance.refetch();

      const errorMessage = getTxErrorMessage(
        error as Error,
        t('TOAST.FAILED_TO_REQUEST_APPROVE_DESC')
      );

      toast({
        title: t('TOAST.FAILED_TO_REQUEST_APPROVE_TITLE'),
        description: errorMessage,
        type: 'error',
      });
    }
  };

  const { switchNetworkAsync } = useSwitchNetwork();

  useEffect(() => {
    switchNetworkAsync({ chainId: vaultChainId });
  }, []);

  const handleSwitchChain = () => {
    return switchNetworkAsync({ chainId: vaultChainId });
  };

  const handleSubmit = async () => {
    if (!canDepositIntoVault(vault)) {
      return;
    }

    if (!isCorrectChain) {
      return handleSwitchChain();
    }

    if (requiredAllowance) {
      return await handleApprove();
    }
    return handleDeposit();
  };

  const valuePerShare =
    bnTokenAmount.gt(0) && previewSwap.data && previewSwap.data.amountOut > 0
      ? parseFloat(tokenAmount) /
        parseFloat(
          ethers.utils.formatUnits(previewSwap.data.amountOut, vault.decimals)
        )
      : 0;

  const zeroAmountOut = previewSwap.data && previewSwap.data.amountOut <= 0;

  const tokenPriceQuery = useTokenPrice({
    tokenAddress: depositToken.address,
    chainId: vaultChainId,
  });

  const depositTokenPrice = tokenPriceQuery.data
    ? Number(tokenPriceQuery.data.formatedPrice)
    : 0;

  const dollarAmount = depositTokenPrice * parseFloat(tokenAmount);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 6,
    }).format(value);
  };

  const minUsdValue =
    parseFloat(ethers.utils.formatUnits(minAmountOut, vault.decimals)) *
    (valuePerShare * depositTokenPrice);
  const expectedUsdValue =
    parseFloat(
      ethers.utils.formatUnits(previewSwap.data?.amountOut || 0, vault.decimals)
    ) *
    (valuePerShare * depositTokenPrice);

  const [detailsOpen, setDetailsOpen] = useState(expandDetails);

  const isApproveLoading =
    isTokenAmountPending() ||
    isSpendingLimitPending() ||
    approve.contract.isPending ||
    approve.prepare.isFetching ||
    form.formState.isSubmitting;

  const isApproveDisabled =
    !canDepositIntoVault(vault) ||
    isApproveLoading ||
    approve.prepare.isError ||
    approve.contract.isPending ||
    !approveRequest ||
    !isDebouncedValid;

  const isDepositLoading =
    activeSwap.contract.isPending ||
    activeSwap.prepare.isFetching ||
    form.formState.isSubmitting ||
    previewSwap.isFetching ||
    activeSwap.gasQuery.isFetching ||
    isTokenAmountPending();

  const isDepositDisabled =
    !canDepositIntoVault(vault) ||
    isDepositLoading ||
    activeSwap.gasQuery.isError ||
    activeSwap.prepare.isError ||
    !depositRequest ||
    !isDebouncedValid ||
    !isTokenAmountSynced ||
    zeroAmountOut;

  return (
    <form onSubmit={form.handleSubmit(handleSubmit)}>
      <ModalV2.Body variant='dark'>
        <div className='mb-4 flex flex-col justify-end gap-1'>
          <Title4 className='pb-1 text-right text-[1rem]'>{vault.name}</Title4>
          <div className='flex flex-row justify-end gap-1'>
            <Body2>RiskLevel:</Body2>
            <Body2 className={cx('', getRiskLevelColorClass(vault.riskLevel))}>
              {getRiskLevelLabel(vault.riskLevel)}
            </Body2>
            <Body2>APY:</Body2>
            <VaultApy className='text-[1.125rem]' vault={vault} />
          </div>
        </div>
        {!readOnlyInput && (
          <div className='flex flex-row items-center justify-end gap-1 pb-2'>
            <Body3 className='text-white-500'>
              {t('MODAL_DEPOSIT.BALANCE')}:
            </Body3>

            <InputBalanceLabel chainId={vaultChainId} token={depositToken} />

            <div className='cursor-pointer' onClick={handleClickMax}>
              <Button style='text' className='px-0 py-0'>
                MAX
              </Button>
            </div>
          </div>
        )}
        <InputTokenAmount
          readOnly={readOnlyInput}
          tokenSymbol={depositToken.symbol}
          control={form.control}
          error={form.formState.errors.tokenAmount?.message}
          endEnhancer={
            <div className='flex items-center gap-1'>
              <span className='text-sm font-normal text-white-500'>
                {depositTokenPrice > 0 && formatCurrency(dollarAmount)}
              </span>
              {!readOnlyInput && (
                <ButtonIcon
                  className='-mr-[8px]'
                  onClick={() => setSelectTokenOpen((value) => !value)}
                >
                  <Icon
                    icon='arrow_down'
                    width={16}
                    height={16}
                    color='#D9D9D9'
                  />
                </ButtonIcon>
              )}
            </div>
          }
          {...form.register('tokenAmount')}
        />

        <SelectToken
          open={selectTokenOpen}
          chainId={vaultChainId}
          value={depositToken}
          options={availableDepositTokenList}
          onClickOutside={() => setSelectTokenOpen(false)}
          onChange={handleTokenChange}
        />

        <div className='mb-2 mt-8 flex flex-row items-center justify-between'>
          <Title4 className='pb-1 text-[1rem]'>{t('DETAILS.DETAILS')}</Title4>
          <ButtonIcon onClick={() => setDetailsOpen((prev) => !prev)}>
            <Icon
              icon='arrow_down'
              height={24}
              width={24}
              color='#B5B5B5'
              className={cx('transition-all duration-500 ease-in-out', {
                '-rotate-180': detailsOpen,
              })}
            />
          </ButtonIcon>
        </div>

        <Collapse visible={detailsOpen} type='vertical'>
          <div className='flex flex-col gap-3'>
            <div className='flex flex-col gap-1'>
              <ModalDetailsRow
                label={t('DETAILS.SLIPPAGE_TOLERANCE')}
                value={
                  <div className='flex flex-row items-center gap-1'>
                    <div>{form.getValues('slippage')}%</div>
                    <ButtonIcon
                      className='px-0 py-0'
                      onClick={() => setSlippageOpen((open) => !open)}
                    >
                      <Icon
                        icon='settings'
                        width={16}
                        height={16}
                        color='#D9D9D9'
                      />
                    </ButtonIcon>
                  </div>
                }
              />
              <Collapse visible={slippageOpen} type={'vertical'}>
                <SlippageInput
                  onChangeSlippage={handleChangeSlippage}
                  value={form.getValues('slippage')}
                  {...form.register('slippage')}
                />
              </Collapse>
            </div>
            {isCorrectChain && requiredAllowance && (
              <div className='flex flex-col gap-1'>
                <ModalDetailsRow
                  label={t('DETAILS.SPEND_LIMIT_PERMISSION')}
                  value={
                    <div className='flex flex-row items-center gap-1'>
                      <div>{tokenAmount}</div>
                      <ButtonIcon
                        className='px-0 py-0'
                        onClick={toggleSpendLimit}
                      >
                        <Icon
                          icon={spendLimitOpen ? 'arrow_drop_up' : 'edit'}
                          width={16}
                          height={16}
                          color='#D9D9D9'
                        />
                      </ButtonIcon>
                    </div>
                  }
                />
                <Collapse visible={spendLimitOpen} type={'vertical'}>
                  <SpendLimitInput
                    defaultSpendLimit={tokenAmount}
                    customSpendLimit={
                      Number(customSpendingLimit) > 0
                        ? customSpendingLimit ?? '0'
                        : tokenAmount
                    }
                    onSpendLimitChange={(value) =>
                      form.setValue('spendingLimit', value)
                    }
                  />
                </Collapse>
              </div>
            )}
            <ModalDetailsRow
              label={t('DETAILS.PRICE_PER_SHARE')}
              value={
                <div className='flex flex-row items-center gap-1'>
                  {formatValueToSixDecimals(valuePerShare)}
                  <TokenImage
                    symbol={depositToken.symbol}
                    src={getTokenIconUrl(depositToken.symbol)}
                    alt={depositToken.symbol}
                    width={16}
                    height={16}
                  />
                </div>
              }
            />

            <HorizontalLine />
            <ModalDetailsRow
              label={t('DETAILS.MINIMUM_AMOUNT_OUT')}
              value={
                <div className='flex flex-col gap-1'>
                  <div className='self-end'>
                    {ethers.utils.formatUnits(
                      minAmountOut || 0,
                      vault.decimals
                    )}
                  </div>
                  <div className='ml-0.5 self-end text-xs text-white-400'>
                    {formatCurrency(minUsdValue)}
                  </div>
                </div>
              }
            />
            <ModalDetailsRow
              label={t('DETAILS.EXPECTED_AMOUNT_OUT')}
              value={
                <div className='flex flex-col gap-1'>
                  <div className='self-end'>
                    {ethers.utils.formatUnits(
                      previewSwap.data?.amountOut || 0,
                      vault.decimals
                    )}
                  </div>
                  <div className='ml-0.5 self-end text-xs text-white-400'>
                    {formatCurrency(expectedUsdValue)}
                  </div>
                </div>
              }
            />
          </div>
        </Collapse>
      </ModalV2.Body>
      <div className='flex flex-col py-2'>
        {previewSwap.error && (
          <Body2 className='whitespace-break-spaces text-red-600'>
            {getTxErrorMessage(
              previewSwap.error,
              'Error occurred while fetching swap data'
            )}
          </Body2>
        )}

        {depositPreparationError && (
          <Body2 className='whitespace-break-spaces text-red-600'>
            {getTxErrorMessage(
              depositPreparationError,
              'Error occurred while fetching swap data'
            )}
          </Body2>
        )}

        {zeroAmountOut && (
          <Body2 className='text-red-600'>
            {t('MODAL_DEPOSIT.ERROR_ZERO_AMOUNT_OUT')}
          </Body2>
        )}
      </div>
      <div className='mt-8 flex justify-end gap-4'>
        <Button onClick={() => handleOpenChange(false)} style='outline'>
          {t('MODAL_DEPOSIT.BUTTON_CANCEL')}
        </Button>

        {!isCorrectChain ? (
          <Button type='button' onClick={handleSwitchChain}>
            {t('MODAL_DEPOSIT.BUTTON_SWITCH_NETWORK')}
          </Button>
        ) : null}

        {isCorrectChain && requiredAllowance ? (
          <Button
            loading={isApproveLoading}
            disabled={isApproveDisabled}
            type='submit'
          >
            {t('MODAL_DEPOSIT.BUTTON_APPROVE')}
          </Button>
        ) : null}

        {isCorrectChain && !requiredAllowance ? (
          <Button
            loading={isDepositLoading}
            disabled={isDepositDisabled}
            type='submit'
          >
            {t('MODAL_DEPOSIT.BUTTON_DEPOSIT')}
          </Button>
        ) : null}
      </div>
    </form>
  );
};

const VaultApy = (props: { vault: DynaVault; className?: string }) => {
  const { vault, className } = props;

  const apy = getVaultAPY(vault);
  const apyPrefix = getVaultAPYPrefix(vault, apy);

  return (
    <Body2
      className={cx(
        twMerge('font-bold', apy > 0 ? 'text-green' : 'text-white', className)
      )}
    >
      {apyPrefix}
      {formatPercent(apy.toString())}%
    </Body2>
  );
};
